import {
  memo, useMemo, useRef, useCallback,
} from 'react';
import { Box, Typography } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';

import { mapIconKey, mapIcons } from '../map/core/preloadImages';
import { getOperationalStatusMeta, toKph } from '../common/util/deviceVisualStatus';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useAttributePreference } from '../common/util/preferences';

const DRAG_THRESHOLD = 8;

const STATUS_ICONS = {
  ON_MOVING: AutorenewIcon,
  OFF: PowerSettingsNewIcon,
  ON_STOPPED: PauseCircleFilledIcon,
  IDLE_10M: PauseCircleFilledIcon,
  OUT_12H: WifiOffIcon,
  OUT_24H: WifiOffIcon,
  OUT_72H: WifiOffIcon,
  NO_DATA: ErrorOutlineIcon,
};

const formatFieldValue = (value, t) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value ? t('sharedYes') : t('sharedNo');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const getDeviceFieldValue = (device, fieldKey, t) => {
  if (!fieldKey) return null;

  if (fieldKey.startsWith('attributes.')) {
    const attributeKey = fieldKey.slice('attributes.'.length);
    return formatFieldValue(device?.attributes?.[attributeKey], t);
  }

  return formatFieldValue(device?.[fieldKey], t);
};

const DeviceRowCompact = memo(({
  device,
  position,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const devicePrimary = useAttributePreference('devicePrimary', 'name');
  const deviceSecondary = useAttributePreference('deviceSecondary', 'uniqueId');

  const pointerRef = useRef({
    startX: 0,
    startY: 0,
    moved: false,
    tracking: false,
  });

  const status = useMemo(
    () => getOperationalStatusMeta(device, position),
    [device, position],
  );

  const speedKph = useMemo(() => toKph(position?.speed), [position?.speed]);
  const speedLabel = speedKph == null ? '— km/h' : `${Math.round(speedKph)} km/h`;

  const title = useMemo(() => (
    getDeviceFieldValue(device, devicePrimary, t)
    || getDeviceFieldValue(device, 'name', t)
    || `#${device?.id ?? ''}`
  ), [device, devicePrimary, t]);

  const subtitle = useMemo(() => (
    getDeviceFieldValue(device, deviceSecondary, t)
  ), [device, deviceSecondary, t]);

  const icon = useMemo(
    () => mapIcons[mapIconKey(device?.category)] || mapIcons.default,
    [device?.category],
  );

  const StatusIcon = STATUS_ICONS[status.key] || ErrorOutlineIcon;

  const startPointerTracking = (clientX, clientY) => {
    pointerRef.current = {
      startX: clientX,
      startY: clientY,
      moved: false,
      tracking: true,
    };
  };

  const updatePointerTracking = (clientX, clientY) => {
    if (!pointerRef.current.tracking) return;

    const dx = Math.abs(clientX - pointerRef.current.startX);
    const dy = Math.abs(clientY - pointerRef.current.startY);

    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
      pointerRef.current.moved = true;
    }
  };

  const stopPointerTracking = () => {
    pointerRef.current.tracking = false;
  };

  const handleSelect = useCallback(() => {
    if (pointerRef.current.moved) return;

    if (onSelect && device?.id != null) {
      onSelect(device.id);
    }
  }, [onSelect, device]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (onSelect && device?.id != null) {
        onSelect(device.id);
      }
    }
  }, [onSelect, device]);

  return (
    <Box
      onMouseDown={(e) => startPointerTracking(e.clientX, e.clientY)}
      onMouseMove={(e) => updatePointerTracking(e.clientX, e.clientY)}
      onMouseUp={stopPointerTracking}
      onMouseLeave={stopPointerTracking}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        if (touch) startPointerTracking(touch.clientX, touch.clientY);
      }}
      onTouchMove={(e) => {
        const touch = e.touches[0];
        if (touch) updatePointerTracking(touch.clientX, touch.clientY);
      }}
      onTouchEnd={stopPointerTracking}
      onTouchCancel={stopPointerTracking}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 0.9,
        px: 1,
        py: 0.85,
        borderRadius: 1.5,
        border: selected ? '1px solid' : '1px solid transparent',
        borderColor: selected ? 'primary.main' : 'transparent',
        backgroundColor: selected ? 'action.selected' : 'transparent',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
        '&:hover': {
          backgroundColor: 'action.hover',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{
            width: 20,
            height: 20,
            display: 'block',
          }}
        />
      </Box>

      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.15,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" noWrap sx={{ opacity: 0.75 }}>
            {subtitle}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            minHeight: 16,
            mt: 0.1,
          }}
        >
          <StatusIcon fontSize="inherit" style={{ fontSize: 14, color: status.uiColor }} />
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: status.uiColor,
              fontWeight: 600,
              lineHeight: 1.1,
            }}
          >
            {status.label}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
          flexShrink: 0,
          minHeight: 52,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            width: 86,
            textAlign: 'right',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {speedLabel}
        </Typography>

      </Box>
    </Box>
  );
});

export default DeviceRowCompact;
